import clsx from 'clsx'
import { computed, defineComponent } from 'vue'

import VButton from '@/components/common/button/VButton'
import VIcon from '@/components/common/VIcon'

interface Props {
  itemsPerPage: number
  totalItemsNumber: number
  currentPage: number
  optionalTotalPages?: number
  onCurrentPageChange: (value: number) => void
}

export default defineComponent<Props>((props) => {
  // #region Getters

  const totalPages = computed((): number => {
    if (props.optionalTotalPages !== undefined) {
      return props.optionalTotalPages
    }

    const totalPages = Math.ceil(props.totalItemsNumber / props.itemsPerPage)
    return totalPages > 0 ? totalPages : 1
  })

  const isFirstPage = computed((): boolean => {
    return props.currentPage === 1
  })

  const isLastPage = computed((): boolean => {
    return Math.ceil(totalPages.value) <= props.currentPage
  })

  const mapTotalPageItems = computed((): number[] => {
    return Array.from({ length: totalPages.value }, (_, i) => i + 1).splice(0, 5)
  })

  // #endregion

  return () => (
    <div
      t-data='v-pagination'
      class='flex items-center space-x-2'
    >
      <VButton
        t-data='v-pagination-first-page-btn'
        class='btn-small'
        color={clsx('btn-white')}
        disabled={isFirstPage.value}
        onClick={() => { props.onCurrentPageChange(1) }}
      >
        <VIcon
          class='text-[24px]'
          icon='material-symbols-light:skip-previous'
        />
      </VButton>
      <VButton
        t-data='v-pagination-previous-btn'
        class='btn-small'
        color={clsx('btn-white')}
        disabled={isFirstPage.value}
        onClick={() => { props.onCurrentPageChange(props.currentPage - 1) }}
      >
        <VIcon
          class='text-[24px]'
          icon='material-symbols-light:chevron-left'
        />
      </VButton>
      {mapTotalPageItems.value.map((page) => (
        <VButton
          key={page}
          t-data='v-pagination-page-btn'
          class={clsx(
            'btn-small rounded-full',
            props.currentPage === page ? '!bg-black/20' : ''
          )}
          color={clsx('btn-white')}
          onClick={() => {
            props.onCurrentPageChange(page)
          }}
        >
          {page}
        </VButton>
      ))}
      <VButton
        t-data='v-pagination-next-btn'
        class='btn-small'
        color={clsx('btn-white')}
        disabled={isLastPage.value}
        onClick={() => { props.onCurrentPageChange(props.currentPage + 1) }}
      >
        <VIcon
          class='text-[24px]'
          icon='material-symbols-light:chevron-right'
        />
      </VButton>
      <VButton
        t-data='v-pagination-last-page-btn'
        class='btn-small'
        color={clsx('btn-white')}
        onClick={() => { props.onCurrentPageChange(totalPages.value) }}
        disabled={isLastPage.value}
      >
        <VIcon
          class='text-[24px]'
          icon='material-symbols-light:skip-next'
        />
      </VButton>
    </div>
  )
}, {
  name: 'VPagination',
  props: ['itemsPerPage', 'currentPage', 'totalItemsNumber', 'optionalTotalPages', 'onCurrentPageChange']
})
